import { useState } from "react";
import {
  ASSET_KIND_LABELS,
  ASSET_KINDS,
  LIBRARY_UNIT_LABELS,
  LIBRARY_UNITS,
  type AssetBulkBody,
  type AssetDto,
  type AssetKind,
  type AssetType,
  type BuildingDto,
  type LibraryUnit,
} from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { api, ApiError } from "../../lib/api";
import { hasModule, useMe } from "../../lib/auth";
import { useToast } from "../../lib/toast";
import { useBuildingCache } from "../building/useBuilding";
import { ElementPicker } from "../elements/ElementPicker";
import { AssetTypePicker } from "./AssetTypePicker";

/** Bulkactie in de assetlijst; plaatsen op de tekening gaat via de tekening zelf (één voor één). */
export type BulkMode = "type" | "data" | "parent";

const TITLE: Record<BulkMode, string> = {
  type: "Type aanpassen",
  data: "Gegevens aanvullen",
  parent: "Onder een hoofdasset",
};

const n = (k: number, one: string, more: string) => `${k} ${k === 1 ? one : more}`;

/**
 * Eén wijziging voor alle gekozen assets (POST /buildings/:id/assets/bulk). Lege velden blijven
 * ongewijzigd; bij *Gegevens aanvullen* houdt een asset standaard wat al is ingevuld.
 */
export function BulkDialog({
  building: b,
  mode,
  assets,
  onClose,
}: {
  building: BuildingDto;
  mode: BulkMode;
  assets: AssetDto[];
  onClose: () => void;
}) {
  const { data: me } = useMe();
  const toast = useToast();
  const cache = useBuildingCache(b.id);
  const [busy, setBusy] = useState(false);
  const ids = new Set(assets.map((a) => a.id));

  // Type: soort en element.
  const [kind, setKind] = useState<AssetKind | "">("");
  const [element, setElement] = useState<string | null>(null);
  /** Wat is het? (symbool, docs/SYMBOLEN.md); vult een leeg element in. */
  const [assetType, setAssetType] = useState<AssetType | null>(null);
  // Gegevens.
  const [d, setD] = useState({
    brand: "",
    model: "",
    capacity: "",
    yearBuilt: "",
    warrantyUntil: "",
    quantity: "",
    unit: "" as LibraryUnit | "",
    floorId: "",
    roomId: "",
    notes: "",
  });
  const [onlyEmpty, setOnlyEmpty] = useState(true);
  // Hoofdasset ("" = nog niet gekozen, "none" = zelfstandig maken).
  const [parent, setParent] = useState("");

  const set = (k: keyof typeof d) => (e: { target: { value: string } }) => setD({ ...d, [k]: e.target.value });
  const year = d.yearBuilt.trim() ? Number(d.yearBuilt) : null;
  const yearOk = year === null || (Number.isInteger(year) && year >= 1800 && year <= 2100);
  const qty = d.quantity.trim() ? Number(d.quantity.trim().replace(",", ".")) : null;
  const qtyOk = qty === null || (Number.isFinite(qty) && qty >= 0);
  const rooms = b.rooms.filter((r) => r.floorId === d.floorId);

  // Onderdelen (docs/ASSETS-MJOP.md): één niveau, dus geen assets met eigen onderdelen in de selectie,
  // en alleen zelfstandige assets buiten de selectie als hoofdasset.
  const withParts = assets.filter((a) => b.assets.some((x) => x.parentAssetId === a.id));
  const parents = b.assets.filter((x) => !x.parentAssetId && !ids.has(x.id));
  const anyPart = assets.some((a) => a.parentAssetId);

  function body(): AssetBulkBody["set"] | null {
    if (mode === "type") {
      const s: AssetBulkBody["set"] = {};
      if (kind) s.assetKind = kind;
      if (element) s.elementCode = element;
      if (assetType) s.assetType = assetType;
      return Object.keys(s).length ? s : null;
    }
    if (mode === "parent") return parent ? { parentAssetId: parent === "none" ? null : parent } : null;
    const s: AssetBulkBody["set"] = {};
    for (const k of ["brand", "model", "capacity", "warrantyUntil", "notes"] as const)
      if (d[k].trim()) s[k] = d[k].trim();
    if (year !== null) s.yearBuilt = year;
    if (qty !== null) s.quantity = qty;
    if (d.unit) s.unit = d.unit;
    if (d.floorId) {
      s.floorId = d.floorId;
      if (d.roomId) s.roomId = d.roomId;
    }
    return Object.keys(s).length ? s : null;
  }
  const change = body();
  const valid = mode !== "data" || (yearOk && qtyOk);
  const blocked = mode === "parent" && parent && parent !== "none" && withParts.length > 0;

  async function save() {
    if (!change) return;
    setBusy(true);
    try {
      const out = await api<AssetDto[]>(`/buildings/${b.id}/assets/bulk`, {
        method: "POST",
        body: { ids: assets.map((a) => a.id), set: change, ...(mode === "data" ? { onlyEmpty } : {}) },
      });
      out.forEach((a) => cache.upsertAsset(a));
      toast(
        mode === "parent" && parent !== "none"
          ? `${n(out.length, "asset is", "assets zijn")} nu onderdeel van ${b.assets.find((x) => x.id === parent)?.code ?? "de hoofdasset"}`
          : `${n(out.length, "asset", "assets")} bijgewerkt`,
      );
      onClose();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Er ging iets mis");
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`${TITLE[mode]} · ${n(assets.length, "asset", "assets")}`}
      onClose={onClose}
      busy={busy}
      wide={mode !== "parent"}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={save}
            disabled={busy || !change || !valid || Boolean(blocked)}
          >
            {mode === "parent" && parent === "none" ? "Losmaken" : `Toepassen op ${assets.length}`}
          </button>
        </>
      }
    >
      <p className="muted small bulkpicked">
        {assets
          .slice(0, 6)
          .map((a) => a.code)
          .join(", ")}
        {assets.length > 6 ? ` en nog ${assets.length - 6}` : ""}
      </p>

      {mode === "type" && (
        <>
          <label className="field">
            <span>Soort</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as AssetKind | "")} data-testid="bulk-kind">
              <option value="">— Niet wijzigen —</option>
              {ASSET_KINDS.filter((k) => k !== "meter" || hasModule(me, "meters")).map((k) => (
                <option key={k} value={k}>
                  {ASSET_KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </label>
          <p className="label">Wat is het?</p>
          <AssetTypePicker value={assetType} onChange={setAssetType} />
          <span className="hint">Het symbool. Een asset zonder element krijgt ook het element van wat je kiest.</span>
          <p className="label">Element (NLBE-SfB)</p>
          <ElementPicker value={element} onChange={setElement} full={me?.org?.elementCoding ?? false} />
          <span className="hint">Kies je geen element, dan houdt elke asset zijn eigen element.</span>
        </>
      )}

      {mode === "data" && (
        <>
          <p className="hint">Vul alleen in wat voor alle gekozen assets geldt. Lege velden blijven zoals ze zijn.</p>
          <label className="check">
            <input type="checkbox" checked={onlyEmpty} onChange={(e) => setOnlyEmpty(e.target.checked)} />
            <span>Alleen lege velden aanvullen (wat al is ingevuld blijft staan)</span>
          </label>
          <div className="grid2">
            <label className="field">
              <span>Merk</span>
              <input value={d.brand} onChange={set("brand")} />
            </label>
            <label className="field">
              <span>Type</span>
              <input value={d.model} onChange={set("model")} />
            </label>
            <label className="field">
              <span>Capaciteit</span>
              <input value={d.capacity} onChange={set("capacity")} placeholder="bijv. 65 kW, 630 kg, 400 l" />
            </label>
            <label className="field">
              <span>Bouwjaar</span>
              <input
                inputMode="numeric"
                value={d.yearBuilt}
                onChange={set("yearBuilt")}
                placeholder="bijv. 2018"
                aria-invalid={!yearOk}
              />
            </label>
            <label className="field">
              <span>Eind garantie</span>
              <input type="date" value={d.warrantyUntil} onChange={set("warrantyUntil")} />
            </label>
            <div className="row">
              <label className="field grow">
                <span>Hoeveelheid</span>
                <input inputMode="decimal" value={d.quantity} onChange={set("quantity")} aria-invalid={!qtyOk} />
              </label>
              <label className="field">
                <span>Eenheid</span>
                <select value={d.unit} onChange={(e) => setD({ ...d, unit: e.target.value as LibraryUnit | "" })}>
                  <option value="">—</option>
                  {LIBRARY_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {LIBRARY_UNIT_LABELS[u]}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="field">
              <span>Verdieping</span>
              <select value={d.floorId} onChange={(e) => setD({ ...d, floorId: e.target.value, roomId: "" })}>
                <option value="">— Niet wijzigen —</option>
                {b.floors.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Ruimte</span>
              <select value={d.roomId} onChange={set("roomId")} disabled={!d.floorId}>
                <option value="">—</option>
                {rooms.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="field">
            <span>Notities</span>
            <textarea rows={2} value={d.notes} onChange={set("notes")} />
          </label>
          {!onlyEmpty && d.floorId && (
            <p className="hint">Assets die naar een andere verdieping gaan, verliezen hun plek op de tekening.</p>
          )}
        </>
      )}

      {mode === "parent" && (
        <>
          <label className="field">
            <span>Hoofdasset</span>
            <select value={parent} onChange={(e) => setParent(e.target.value)} data-testid="bulk-parent">
              <option value="">— Kies de hoofdasset —</option>
              {anyPart && <option value="none">— Zelfstandige assets maken —</option>}
              {parents.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.code} {x.name}
                </option>
              ))}
            </select>
            <span className="hint">
              {parent === "none"
                ? "De onderdelen krijgen elk een eigen nummer."
                : "De gekozen assets worden onderdelen, met de code van de hoofdasset en een volgnummer (bijv. B6Y-7CG.1, B6Y-7CG.2). Taken en keuringen blijven bij de hoofdasset."}{" "}
              Een QR-code op een geprint label blijft werken, de code die erop staat niet.
            </span>
          </label>
          {withParts.length > 0 && parent !== "none" && (
            <p className="hint" role="alert">
              {withParts.map((a) => a.code).join(", ")} {withParts.length === 1 ? "heeft" : "hebben"} zelf onderdelen en{" "}
              {withParts.length === 1 ? "kan" : "kunnen"} geen onderdeel worden. Haal{" "}
              {withParts.length === 1 ? "die" : "ze"} uit de selectie.
            </p>
          )}
        </>
      )}
    </Modal>
  );
}
